import { useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { providers } from "ethers";
import axios from "axios";
import { clearToken, selectAccessToken, selectWalletAddress } from "../components/connectWallet/store/slice";
import { clearLocalStoragePreservingReferral } from "../utils/localStorage";
import { VITE_API_URL } from "@/utils/constant";
const AUTH_STATUS_URL = "/profiles/current/me";
const HEARTBEAT_INTERVAL_MS = 3_000;

/**
 * Periodically polls the auth-status endpoint every 10 s.
 * If the server returns 401 the user's session is considered expired:
 * tokens are cleared from Redux and localStorage without any page redirect
 * so the app can react naturally (protected routes will redirect on their own).
 *
 * Mount this hook exactly once, at the top-level router component, so it
 * stays active across all pages without being re-registered on navigation.
 */
export function useAuthHeartbeat(): void {
  const dispatch = useDispatch();
  const accessToken = useSelector(selectAccessToken);
  const storedWallet = useSelector(selectWalletAddress);

  // Keep stable refs so the interval callback always reads the latest values
  // without needing to be re-created every time they change.
  const tokenRef = useRef(accessToken);
  const storedWalletRef = useRef(storedWallet);

  useEffect(() => {
    tokenRef.current = accessToken;
  }, [accessToken]);

  useEffect(() => {
    storedWalletRef.current = storedWallet;
  }, [storedWallet]);

  useEffect(() => {
    const check = async () => {
      console.log(`Pooling access token validate`)
      const token = tokenRef.current;
      if (!token) return;
      console.log(`Prepare fetch to: ${VITE_API_URL}${AUTH_STATUS_URL}`);
      try {
        const response: any = await axios.get(`${VITE_API_URL}${AUTH_STATUS_URL}`, {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const stored = storedWalletRef.current?.toLowerCase();
        if (stored && window.ethereum) {
          try {
            const web3Provider = new providers.Web3Provider(window.ethereum);
            const signer = await web3Provider.getSigner();
            console.log(`wallet current web3: ${(await signer?.getAddress()).toLowerCase()}`)
            console.log(`wallet store: ${stored}`)
            console.log(`wallet profile: ${response?.data?.data?.user?.walletAddress}`)
            const onchain = (await signer.getAddress()).toLowerCase();
            if (onchain !== stored) {
              clearLocalStoragePreservingReferral();
              dispatch(clearToken());
            }
          } catch {
            clearLocalStoragePreservingReferral();
            dispatch(clearToken());
          }
        } else {
          clearLocalStoragePreservingReferral();
          dispatch(clearToken());
        }

        if (response.status === 401) {
          clearLocalStoragePreservingReferral();
          dispatch(clearToken());
        }
      } catch (error) {
        console.log(error);
        // Network errors are silently ignored; the app keeps running.
      }
    };

    const intervalId = setInterval(check, HEARTBEAT_INTERVAL_MS);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);
}
